import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { signIn } from '@/shared/lib/auth';
import { useLogo } from '@/shared/hooks/useLogo';
import { empresaService } from '@/shared/services/empresaService';
import { AppPageLoading, AppShell } from '@/shared/components/layout';

export default function Login() {
  const { session, role, empresaId, loading } = useAuth();
  const navigate = useNavigate();
  const logoUrl = useLogo();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!session) return;

    if (role === 'super_admin') {
      navigate('/admin-master', { replace: true });
      return;
    }

    if (role === 'tenant') {
      if (!empresaId) {
        setError('Usuário tenant sem empresa vinculada.');
        return;
      }
      empresaService.buscarStatus(empresaId)
        .then(status => {
          if (status === 'suspenso') {
            navigate('/conta-suspensa', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        })
        // Falha ao consultar o status: segue para o /dashboard — o TenantStatusGuard faz a mesma consulta, falha
        // fechado e mostra "Tentar novamente"; mostrar erro aqui deixaria o usuário logado na tela de login.
        .catch(() => navigate('/dashboard', { replace: true }));
      return;
    }

    // Sem fallback para user_metadata.role: o ProtectedRoute só aceita a role
    // do JWT, então navegar por ela gerava loop /login ↔ /dashboard — e
    // user_metadata é editável pelo próprio usuário.
    setError('Usuário sem role definida. Configure o Auth Hook e o registro em user_empresas.');
  }, [session, role, empresaId, loading, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);

    try {
      const { error: authError } = await signIn(email.trim(), password);
      if (authError) {
        setError('E-mail ou senha inválidos.');
      }
    } catch {
      setError('Não foi possível entrar agora. Verifique sua conexão e tente novamente.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <AppPageLoading />;

  return (
    <AppShell ambient>
      <div className="relative flex min-h-dvh flex-col items-center justify-center px-4 py-10 pb-[calc(2.5rem+env(safe-area-inset-bottom))] pt-[calc(2.5rem+env(safe-area-inset-top))]">
        <div className="relative w-full max-w-sm">
          <div className="animate-fade-in mb-8 text-center">
            <img
              src={logoUrl}
              alt="Espetos Grill"
              className="mx-auto mb-3 h-24 w-auto object-contain drop-shadow-2xl sm:h-28"
            />
            <p className="mt-1 text-sm text-slate-400">DS Espetos Grill · Digital Signage</p>
          </div>

          <div
            className="animate-fade-in-up rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-7"
            style={{ animationDelay: '80ms' }}
          >
            <h1 className="mb-5 text-xl font-semibold text-white">Entrar na plataforma</h1>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="login-email"
                  className="mb-1.5 block text-sm font-medium text-slate-400"
                >
                  E-mail
                </label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'login-error' : undefined}
                  autoComplete="email"
                  placeholder="seu@email.com"
                  className="
                    min-h-11 w-full rounded-xl border border-white/10 bg-white/[0.05]
                    px-3.5 py-2.5 text-sm text-white placeholder-slate-400
                    transition-all duration-300
                    focus:border-orange-500/50 focus:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-orange-500/30
                  "
                />
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="mb-1.5 block text-sm font-medium text-slate-400"
                >
                  Senha
                </label>
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'login-error' : undefined}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="
                    min-h-11 w-full rounded-xl border border-white/10 bg-white/[0.05]
                    px-3.5 py-2.5 text-sm text-white placeholder-slate-400
                    transition-all duration-300
                    focus:border-orange-500/50 focus:bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-orange-500/30
                  "
                />
              </div>

              {error && (
                <div
                  id="login-error"
                  role="alert"
                  className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-3.5 py-2.5"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 text-red-400" strokeWidth={2} />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="
                  flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-orange-500 py-2.5
                  text-sm font-semibold text-white transition-all duration-300
                  hover:bg-orange-400 active:bg-orange-600 active:scale-[0.98]
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50
                  disabled:cursor-wait disabled:opacity-50
                "
              >
                {busy ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            Acesso restrito a colaboradores autorizados
          </p>
        </div>
      </div>
    </AppShell>
  );
}
